import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, BookOpen, Users, Trophy, LogOut, Settings, UserCog, Sparkles, TrendingUp, Megaphone } from "lucide-react";

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Administrateur", mentor: "Mentor", eleve: "Élève", communication: "Chargé de Communication" };
const ROLE_COLOR = { super_admin: "#FF8C42", admin: "#00a86b", mentor: "#00a86b", eleve: "#00a86b", communication: "#7c3aed" };

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const canManage = ["super_admin","admin","mentor"].includes(user?.role);
  const canManageAds = ["super_admin","communication"].includes(user?.role);

  const nav_items = [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/courses", label: "Cours", icon: BookOpen },
    ...(canManage ? [{ to: "/app/users", label: "Utilisateurs", icon: Users }] : []),
    ...(["super_admin","admin"].includes(user?.role) ? [{ to: "/app/admin/courses", label: "Admin cours", icon: Settings }] : []),
    ...(["super_admin","admin"].includes(user?.role) ? [{ to: "/app/admin/progress", label: "Progression", icon: TrendingUp }] : []),
    ...(canManageAds ? [{ to: "/app/admin/ads", label: "Publicités", icon: Megaphone }] : []),
    ...(user?.role === "super_admin" ? [{ to: "/app/admin/settings", label: "Paramètres", icon: Sparkles }] : []),
    { to: "/app/leaderboard", label: "Classement", icon: Trophy },
    { to: "/app/compte", label: "Mon compte", icon: UserCog },
  ];

  return (
    <div className="min-h-screen bg-[#f8faff] text-[#1a2332] font-body" data-testid="app-shell">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-black/5 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/app" className="flex items-center gap-3 font-headings font-black text-lg text-[#1a2332]">
            <img src="/brand/logo.png" alt="DDSEmpire" className="w-10 h-10 rounded-lg object-contain" />
            <span className="hidden sm:inline">DDSEmpire</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {nav_items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink key={it.to} to={it.to} end={it.end}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s/g,"-")}`}
                  className={({isActive}) => `inline-flex items-center gap-2 px-3 py-2 rounded-lg font-headings font-semibold text-sm transition-colors ${isActive?"bg-[#00a86b] text-white":"text-[#1a2332]/70 hover:text-[#00a86b] hover:bg-[#00a86b]/5"}`}>
                  <Icon className="w-4 h-4" /> {it.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/app/compte" className="hidden md:block text-right hover:opacity-80 transition-opacity" data-testid="account-link">
              <div className="text-sm font-headings font-bold text-[#1a2332]">{user?.name}</div>
              <div className="text-xs font-semibold" style={{color: ROLE_COLOR[user?.role]}}>{ROLE_LABEL[user?.role]} · Mon compte</div>
            </Link>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn"
              className="p-2 rounded-lg bg-[#f8faff] hover:bg-[#00a86b]/10 text-[#1a2332]/80 border border-black/5" aria-label="Déconnexion">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        <nav className="md:hidden flex overflow-x-auto gap-1 px-4 pb-2 border-t border-black/5">
          {nav_items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({isActive}) => `px-3 py-1.5 rounded-lg font-headings font-semibold text-xs whitespace-nowrap ${isActive?"bg-[#00a86b] text-white":"bg-[#f8faff] text-[#1a2332]/70"}`}>
              {it.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
